import {
  GetLocations,
  GetLocationsByClient,
  GetLocationHolidays,
  PostLocation,
  PutLocationById,
  GetLocationById,
  DeleteLocationById,
  GetLocationImages,
  GetLocationImageUploadUrl,
  PostCompleteLocationImageUpload,
  DeleteLocationImage,
  PutLocationImagesOrder,
} from "@/server-types";
import { keepPreviousData } from "@tanstack/react-query";
import { get, post, put, del } from "../request-helpers";
import { useCustomMutation } from "../custom-mutation";
import { useCustomQuery } from "../custom-query";
import { queryClient } from "..";
/**
 * GET /location
 */
export const useGetLocations = (
  query: GetLocations["query"] & {
    enabled?: boolean;
  },
) => {
  return useCustomQuery({
    queryKey: ["locations", query],
    queryFn: () => get<GetLocations>("/location", query),
    placeholderData: keepPreviousData,
    enabled: query.enabled ?? true,
  });
};

/**
 * GET /params/client/locations
 *
 * Narrowed to the locations the caller may view, unlike `useGetLocations`.
 * Use this for any picker whose selection is then read back from the server.
 */
export const useGetLocationsByClient = () => {
  return useCustomQuery({
    queryKey: ["locationsByClient"],
    queryFn: () => get<GetLocationsByClient>("/params/client/locations"),
    placeholderData: keepPreviousData,
  });
};

export const usePostLocation = () => {
  return useCustomMutation({
    mutationFn: (body: PostLocation["body"]) =>
      post<PostLocation>("/location", body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["locations"] });
    },
  });
};

export const usePutLocation = () => {
  return useCustomMutation({
    mutationFn: ({
      locationId,
      body,
    }: {
      locationId: number;
      body: PutLocationById["body"];
    }) => put<PutLocationById>(`/location/${locationId}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["locations"] });
    },
  });
};

export const useGetLocation = ({ locationId }: { locationId: number }) => {
  return useCustomQuery({
    queryKey: ["locations", locationId],
    queryFn: () => get<GetLocationById>(`/location/${locationId}`),
    enabled: locationId != -1,
  });
};

/**
 * GET /location/:locationId/holidays
 *
 * Public holidays for the location's country within `[from, to]`. Returns an
 * empty list when the location has `showHolidays` disabled. Used to mark
 * holiday days in the date-picker calendars.
 */
export const useGetLocationHolidays = (
  {
    locationId,
    from,
    to,
    enabled = true,
  }: {
    locationId: number;
    from: string;
    to: string;
    enabled?: boolean;
  },
  options?: { displayNotification?: boolean },
) => {
  return useCustomQuery({
    queryKey: ["locations", locationId, "holidays", from, to],
    queryFn: () =>
      get<GetLocationHolidays>(`/location/${locationId}/holidays`, {
        from,
        to,
      }),
    placeholderData: keepPreviousData,
    enabled: enabled && Number.isInteger(locationId) && locationId > 0,
    displayNotification: options?.displayNotification,
  });
};

export const useDeleteLocation = () => {
  return useCustomMutation({
    mutationFn: ({ locationId }: { locationId: number }) =>
      del<DeleteLocationById>(`/location/${locationId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["locations"] });
    },
  });
};

/**
 * The location lists and the marketplace screens all render the location's
 * first image, so an image change has to drop them too.
 */
function invalidateLocationImageQueries(locationId: number) {
  queryClient.invalidateQueries({ queryKey: ["locationImages", locationId] });
  queryClient.invalidateQueries({ queryKey: ["locations"] });
  queryClient.invalidateQueries({
    queryKey: ["locationMarketplaceConfig", locationId],
  });
  queryClient.invalidateQueries({ queryKey: ["locationMarketplaceConfigs"] });
}

/**
 * GET /location/:locationId/images
 */
export const useGetLocationImages = (locationId: number | undefined) => {
  return useCustomQuery({
    queryKey: ["locationImages", locationId],
    queryFn: () =>
      get<GetLocationImages>(`/location/${locationId ?? -1}/images`),
    enabled: locationId != null && locationId > 0,
  });
};

/**
 * GET /location/:locationId/images/upload
 */
export const useGetLocationImageUploadUrl = (options?: {
  displayNotification?: boolean;
}) => {
  return useCustomMutation({
    displayNotification: options?.displayNotification,
    mutationKey: ["getLocationImageUploadUrl"],
    mutationFn: ({
      locationId,
      query,
    }: {
      locationId: number;
      query: GetLocationImageUploadUrl["query"];
    }) =>
      get<GetLocationImageUploadUrl>(
        `/location/${locationId}/images/upload`,
        query,
      ),
  });
};

/**
 * POST /location/:locationId/images/upload/complete
 */
export const useCompleteLocationImageUpload = (options?: {
  onSuccess?: (variables: { locationId: number }) => void;
  displayNotification?: boolean;
}) => {
  return useCustomMutation({
    displayNotification: options?.displayNotification,
    mutationKey: ["completeLocationImageUpload"],
    mutationFn: ({
      locationId,
      body,
    }: {
      locationId: number;
      body: PostCompleteLocationImageUpload["body"];
    }) =>
      post<PostCompleteLocationImageUpload>(
        `/location/${locationId}/images/upload/complete`,
        body,
      ),
    onSuccess: (_response, variables) => {
      invalidateLocationImageQueries(variables.locationId);
      options?.onSuccess?.(variables);
    },
  });
};

/**
 * PUT /location/:locationId/images/order
 */
export const usePutLocationImagesOrder = (options?: {
  onSuccess?: (variables: { locationId: number }) => void;
  onError?: () => void;
  displayNotification?: boolean;
}) => {
  return useCustomMutation({
    displayNotification: options?.displayNotification,
    mutationFn: ({
      locationId,
      body,
    }: {
      locationId: number;
      body: PutLocationImagesOrder["body"];
    }) =>
      put<PutLocationImagesOrder>(`/location/${locationId}/images/order`, body),
    onSuccess: (_response, variables) => {
      invalidateLocationImageQueries(variables.locationId);
      options?.onSuccess?.(variables);
    },
    onError: () => options?.onError?.(),
  });
};

/**
 * DELETE /location/:locationId/images/:imageId
 */
export const useDeleteLocationImage = (options?: {
  onSuccess?: (variables: { locationId: number; imageId: number }) => void;
  displayNotification?: boolean;
}) => {
  return useCustomMutation({
    displayNotification: options?.displayNotification,
    mutationFn: ({
      locationId,
      imageId,
    }: {
      locationId: number;
      imageId: number;
    }) => del<DeleteLocationImage>(`/location/${locationId}/images/${imageId}`),
    onSuccess: (_response, variables) => {
      invalidateLocationImageQueries(variables.locationId);
      options?.onSuccess?.(variables);
    },
  });
};
